import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSpareParts, deleteSparePart } from '../../store/slices/inventorySlice';
import { fetchMachines } from '../../store/slices/machinesSlice';
import { SparePart } from '../../types/inventory';
import { StatusBadge } from '../../components/inventory/StatusBadge';
import { LowStockIndicator, filterLowStockItems } from '../../components/inventory/LowStockIndicator';

export const SparePartsOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: spareParts, isLoading } = useAppSelector((state) => state.inventory.spareParts);
  const { machines } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [machineFilter, setMachineFilter] = useState<string>('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SparePart | null>(null);

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    dispatch(fetchMachines({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (machines.length > 0) {
      machines.forEach(machine => {
        dispatch(fetchSpareParts({ machineId: machine.id }));
      });
    }
  }, [dispatch, machines]);

  const handleRefresh = () => {
    machines.forEach(machine => {
      dispatch(fetchSpareParts({ machineId: machine.id }));
    });
  };

  const handleEditItem = (item: SparePart) => {
    navigate(`/machines/${item.machine_id}?tab=spare-parts`);
  };

  const handleDeleteRequest = (item: SparePart) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      await dispatch(deleteSparePart(selectedItem.id));
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      handleRefresh();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const getMachineName = (machineId: string): string => {
    const machine = machines.find(m => m.id === machineId);
    return machine?.name || 'Unknown';
  };

  const handleExport = () => {
    const csvContent = [
      ['Part Number', 'Name', 'Machine', 'Quantity', 'Min Quantity', 'Unit', 'Supplier', 'Price', 'Status'].join(','),
      ...filteredSpareParts.map(item => [
        item.part_number,
        `"${item.name}"`,
        `"${getMachineName(item.machine_id)}"`,
        item.quantity,
        item.min_quantity,
        item.unit,
        item.supplier || '',
        item.price || '',
        item.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ersatzteile_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  let filteredSpareParts = spareParts.filter(item => {
    if (!item) return false;

    const matchesSearch = !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.part_number.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesMachine = machineFilter === 'all' || item.machine_id === machineFilter;

    return matchesSearch && matchesStatus && matchesMachine;
  });

  if (showLowStockOnly) {
    filteredSpareParts = filterLowStockItems(filteredSpareParts);
  }

  const lowStockCount = filterLowStockItems(spareParts).length;

  const columns: GridColDef[] = [
    {
      field: 'part_number',
      headerName: 'Teilenummer',
      width: 130,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'machine_id',
      headerName: 'Maschine',
      width: 180,
      valueGetter: (value: any, row: any) => getMachineName(row.machine_id),
    },
    {
      field: 'quantity',
      headerName: 'Bestand',
      width: 150,
      renderCell: (params) => (
        <LowStockIndicator
          quantity={params.row.quantity}
          minQuantity={params.row.min_quantity}
          unit={params.row.unit}
          inline
        />
      ),
    },
    {
      field: 'min_quantity',
      headerName: 'Min.',
      width: 80,
      valueFormatter: (value: any, row: any) => `${value} ${row.unit}`,
    },
    {
      field: 'supplier',
      headerName: 'Lieferant',
      width: 150,
      valueFormatter: (value: any) => value || '-',
    },
    {
      field: 'price',
      headerName: 'Preis',
      width: 100,
      valueFormatter: (value: any) => value ? `€${value.toFixed(2)}` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <StatusBadge type="spare-part" value={params.value} />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 100,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleEditItem(params.row as SparePart)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => handleDeleteRequest(params.row as SparePart)}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Ersatzteile Übersicht
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={filteredSpareParts.length === 0}
          >
            Export CSV
          </Button>
          <IconButton onClick={handleRefresh} title="Aktualisieren">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Low Stock Alert */}
      {lowStockCount > 0 && !showLowStockOnly && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {lowStockCount} Ersatzteil(e) mit niedrigem Bestand
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Ersatzteile durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Status</MenuItem>
              <MenuItem value="available">Verfügbar</MenuItem>
              <MenuItem value="low_stock">Niedriger Bestand</MenuItem>
              <MenuItem value="out_of_stock">Nicht vorrätig</MenuItem>
              <MenuItem value="discontinued">Eingestellt</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Maschine</InputLabel>
            <Select
              value={machineFilter}
              label="Maschine"
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Maschinen</MenuItem>
              {machines.map((machine) => (
                <MenuItem key={machine.id} value={machine.id}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant={showLowStockOnly ? 'contained' : 'outlined'}
            onClick={() => setShowLowStockOnly(!showLowStockOnly)}
          >
            {showLowStockOnly ? 'Alle anzeigen' : 'Nur niedriger Bestand'}
          </Button>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredSpareParts.length} von {spareParts.length} Ersatzteil(e)
        </Typography>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredSpareParts}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Ersatzteil löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie das Ersatzteil "{selectedItem?.name}" wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
